import { of, forkJoin, throwError } from 'rxjs'
import { catchError, mergeMap, switchMap, map, tap } from 'rxjs/operators'
import DataHelper from 'framework/helpers/data'
import OAuthDeviceFlow from 'framework/oauth2/deviceflow'
import VoltError from 'VoltError'
import Constants from 'api-constants'
import * as factories from '../factories'
import { ERROR_BACKEND_MAPPING } from '../errors'
import { canAccessProtectedHeaders } from 'helpers/device.js'

export default class AuthApi extends OAuthDeviceFlow {
    constructor(config, otherApis = {}) {
        super(config, 'ufinityoauth', otherApis)
        this.config = config
        this.registerErrors(ERROR_BACKEND_MAPPING)
        this.gatewayApi = otherApis.gatewayApi
    }

    /**
     * Retrieves the profile by using authentication token in cache
     * Called systematically by the UI at boot for seeking after authentication skipping authentication screen
     * If the token is missing or invalid, then throw an error to force the UI for authentication
     * @returns {Observable<Profile>}
     */
    getProfile() {
        return this.authenticate().pipe(
            mergeMap(() => this.fetchProfile()),
            catchError((error) => {
                return throwError(
                    new VoltError(VoltError.codes.LOGIN_REQUIRED, {
                        inheritedError: error,
                    })
                )
            })
        )
    }

    /**
     * Fetch the profile
     * @param {Boolean} extended fetch all the profile informations, light profile otherwise
     * @returns {Observable<Profile>}
     */
    fetchProfile(extended = true) {
        const accessToken = this.getAccessToken()
        if (!accessToken) {
            const error = 'Cannot get profile due to empty Access Token'
            this.logger.error('AuthApi', error)
            return throwError(
                new VoltError(VoltError.codes.USER_PROFILE_RETRIEVAL_FAILED, {
                    inheritedError: error,
                })
            )
        }

        const _accountType = DataHelper.getInstance().getData(DataHelper.STORE_KEY.PROFILE_TYPE)
        const _essoId = DataHelper.getInstance().getData(DataHelper.STORE_KEY.ESSOID)
        const _hubid = DataHelper.getInstance().getData(DataHelper.STORE_KEY.HUBID)
        const canFetchFromCache = _accountType && _essoId && _hubid
        if (canFetchFromCache) {
            this.logger.info('Fetch Ufinity profile from cache without reaching the API')
            return of(factories.profileFactoryFromCache(_accountType, _essoId, _hubid, this.config))
        }

        this.logger.info('Fetching Ufinity profile from API....')
        return forkJoin({
            userInfoResponse: this.fetch({
                url: `${this.config.urls.authUrl}/api/v1/user`,
                method: 'POST',
                headers: {
                    'Content-Type':
                        Constants.httpHeader.ContentType.APPLICATION_WWW_FORM_URLENCODED,
                },
                retrieveResponseHeaders: true,
                body: {
                    access_token: accessToken,
                    client_id: this.config.clientId,
                },
                log: 'GET USER INFO',
            }),
            hubIdProfileResponse: extended
                ? this.fetch({
                      url: `${this.config.urls.authUrl}/api/v1/hubidprofile`,
                      method: 'POST',
                      headers: {
                          'Content-Type':
                              Constants.httpHeader.ContentType.APPLICATION_WWW_FORM_URLENCODED,
                      },
                      body: {
                          access_token: accessToken,
                          client_id: this.config.clientId,
                      },
                      log: 'GET HUB ID PROFILE',
                  })
                : of({}),
        }).pipe(
            mergeMap(
                ({
                    userInfoResponse: {
                        response: userInfo = {},
                        headers: userInfoHeaders = {},
                    } = {},
                    hubIdProfileResponse: { response: hubIdProfile = {} } = {},
                }) => {
                    const profile = factories.profileFactory(userInfo, hubIdProfile, this.config)
                    const { userId, email, userAccountType } = profile
                    if (canAccessProtectedHeaders()) {
                        const dateFromHeader = userInfoHeaders['date'] || userInfoHeaders['Date']
                        if (dateFromHeader) {
                            this.gatewayApi.serverTimeStampOffset = dateFromHeader
                        }
                    }

                    return DataHelper.getInstance()
                        .storeData(
                            [
                                [DataHelper.STORE_KEY.ESSOID, userId],
                                [DataHelper.STORE_KEY.PROFILE_TYPE, userAccountType],
                                extended ? [DataHelper.STORE_KEY.HUBID, email] : undefined,
                            ].filter(Boolean)
                        )
                        .pipe(switchMap(() => of(profile)))
                }
            ),
            catchError((error) => {
                return throwError(
                    new VoltError(
                        error.code === VoltError.codes.UNKNOWN_API_ERROR.code
                            ? VoltError.codes.USER_PROFILE_RETRIEVAL_FAILED
                            : error,
                        {
                            inheritedError: error,
                        }
                    )
                )
            })
        )
    }

    /**
     * Username/Password Login
     * @param {Object} data
     * @param {String} data.username
     * @param {String} data.password
     * @param {String} data.oauthToken Oauth Token
     * @param {String} data.oauthRefreshToken Refresh token
     * @param {String} data.oauthTokenExpirationDate Expiration Date (String format: "2022-07-16T16:46:52Z")
     * @param {Object} [data.deviceData] optional device data (brand, model, serial number, casn, etc...)
     * @returns {Observable<String>} Constants.authenticationStatus values
     */
    login({
        username,
        password,
        oauthToken,
        oauthRefreshToken,
        oauthTokenExpirationDate,
        deviceData,
    }) {
        if (oauthToken && oauthRefreshToken) {
            return this._loginFromAppAuth({
                oauthToken,
                oauthRefreshToken,
                oauthTokenExpirationDate,
            })
        }
        if (!username || !password) {
            return throwError(
                new VoltError(VoltError.codes.LOGIN_INVALID_CREDENTIALS, {
                    extraLog: 'Empty Username or/and Password',
                })
            )
        }

        return this.fetch({
            url: `${this.config.urls.authUrl}/oauth/token`,
            method: 'POST',
            headers: {
                'Content-Type': Constants.httpHeader.ContentType.APPLICATION_WWW_FORM_URLENCODED,
            },
            body: {
                grant_type: 'password',
                client_id: this.config.clientId,
                client_secret: this.config.clientSecret,
                username,
                password,
            },
            log: 'LOGIN',
        }).pipe(
            mergeMap(({ response = {} } = {}) => {
                return DataHelper.getInstance()
                    .clearData(this._getStoredKeys())
                    .pipe(
                        mergeMap(() => {
                            return DataHelper.getInstance()
                                .storeData([
                                    [
                                        DataHelper.STORE_KEY.PRIMARY_ACCESS_TOKEN,
                                        response.access_token,
                                    ],
                                    [
                                        DataHelper.STORE_KEY
                                            .PRIMARY_ACCESS_TOKEN_EXPIRATION_TIME_MS,
                                        response.expires_in * 1000 + Date.now(),
                                    ],
                                    [
                                        DataHelper.STORE_KEY.PRIMARY_REFRESH_TOKEN,
                                        response.refresh_token,
                                    ],
                                    [
                                        DataHelper.STORE_KEY.PRIMARY_ACCESS_TOKEN_ISSUED_TIME_MS,
                                        Date.now(),
                                    ],
                                ])
                                .pipe(
                                    switchMap(() => of(Constants.authenticationStatus.SUCCEEDED)),
                                    tap(() => {
                                        this._startAccessTokenRefreshTimer()
                                    })
                                )
                        })
                    )
            })
        )
    }

    /**
     * Please refer to parent method for documentation
     */
    loginWithSecondaryDevice({ deviceId }) {
        let body = {
            device_id: deviceId,
            client_id: this.oauthConfig.clientId,
        }
        return super
            .loginWithSecondaryDevice({
                deviceId,
                forceHeaders: {
                    'Content-Type':
                        Constants.httpHeader.ContentType.APPLICATION_WWW_FORM_URLENCODED,
                },
                forceBody: body,
            })
            .pipe(map((response) => this._overloadVerificationUri(response)))
    }

    /**
     * Please refer to parent method for documentation
     */
    isLoginWithSecondaryDevice() {
        return super
            .isLoginWithSecondaryDevice({
                forceHeaders: {
                    'Content-Type':
                        Constants.httpHeader.ContentType.APPLICATION_WWW_FORM_URLENCODED,
                },
                forcePayload: {
                    grant_type: 'urn:ietf:params:oauth:grant-type:device_code',
                    client_id: this.oauthConfig.clientId,
                    device_id: this.oauthDeviceId,
                    device_code: this.oauthStatus.deviceCode,
                },
            })
            .pipe(map((response) => this._overloadVerificationUri(response)))
    }

    /**
     * Validate user's login via QRcode
     * @link [Doc](https://docs.google.com/document/d/1p2iNYbokUSf1muqmv1O-drx4WkxdYSOl/edit#heading=h.vx1227)
     *
     * @param {Object} options OAuth flow device properties
     * @param {string} options.verificationUri auth data retrieved from loginWithSecondaryDevice() verificationUri
     * @param {string} [options.userCode] user code from verificationUri
     * @param {string} [options.deviceCode] device code from verificationUri
     * @returns {Observable<boolean>}
     */
    validateLoginFromSecondaryDevice({ verificationUri, userCode = '', deviceCode = '' } = {}) {
        const mobileToken = DataHelper.getInstance().getPrimaryAccessToken()

        if (!mobileToken) {
            return throwError(
                new VoltError(VoltError.codes.LOGIN_REQUIRED, {
                    backend: this.backendName,
                })
            )
        }

        return (
            userCode && deviceCode
                ? of({ userCode, deviceCode })
                : this._extractDeviceCodesFromVerificationUri(verificationUri)
        ).pipe(
            mergeMap(({ userCode, deviceCode }) => {
                return this.fetch({
                    url: `${this.oauthConfig.url}/device/authorize`,
                    method: 'POST',
                    body: {
                        device_code: deviceCode,
                        user_code: userCode,
                        mobile_token: mobileToken,
                    },
                    headers: {
                        'Content-Type':
                            Constants.httpHeader.ContentType.APPLICATION_WWW_FORM_URLENCODED,
                    },
                    log: 'DEVICE CODE AUTHORIZATION',
                }).pipe(mergeMap(() => of(true)))
            })
        )
    }

    /**
     * Helper for extracting user and device codes from verificationUri
     *
     * @param {string} verificationUri auth data retrieved from loginWithSecondaryDevice() verificationUri
     * @returns {Observable<Object>} {userCode, deviceCode}
     */
    _extractDeviceCodesFromVerificationUri(verificationUri = '') {
        if (verificationUri.startsWith('http')) {
            return this.fetch({
                url: verificationUri,
            }).pipe(
                switchMap((response) => {
                    const redirectUrl = decodeURIComponent(response.xhr.responseURL)

                    // Extract the codes object from the URL
                    const re = new RegExp(/{(.*)}/s)
                    const match = re.exec(redirectUrl)

                    if (match && match[0]) {
                        return this._extractCodesFromJson(match[0])
                    } else {
                        return throwError(
                            new VoltError(VoltError.codes.FAILED_QR_CODE_VALIDATION_INVALID_CODE, {
                                extraLog: 'Cannot retrieve the QR Code from the Redirect URL',
                                backend: this.backendName,
                            })
                        )
                    }
                }),
                catchError(() =>
                    throwError(
                        new VoltError(VoltError.codes.FAILED_QR_CODE_VALIDATION_INVALID_CODE, {
                            extraLog: 'Cannot retrieve the QR Code from the Redirect URL',
                            backend: this.backendName,
                        })
                    )
                )
            )
        }

        return this._extractCodesFromJson(verificationUri)
    }

    /**
     * Helper for extracting user and device codes from verificationUri
     *
     * @param {string} verificationUri auth data retrieved from loginWithSecondaryDevice() verificationUri
     * @returns {Observable<Object>} {userCode, deviceCode}
     */
    _extractCodesFromJson(verificationUri) {
        if (!verificationUri) {
            const errorMsg = 'You should login with your device at first'
            this.logger.error(OAuthDeviceFlow.TAG, errorMsg)
            return throwError(
                new VoltError(VoltError.codes.FAILED_QR_CODE_VALIDATION, {
                    extraLog: errorMsg,
                    backend: this.backendName,
                })
            )
        }

        let parsedVerificationUri = {}

        try {
            parsedVerificationUri = JSON.parse(verificationUri)
        } catch (e) {
            const errorMsg = 'Error while parsing verificationUri'
            this.logger.error(OAuthDeviceFlow.TAG, errorMsg, e)
            return throwError(
                new VoltError(VoltError.codes.FAILED_QR_CODE_VALIDATION, {
                    extraLog: errorMsg,
                    backend: this.backendName,
                })
            )
        }

        const { deviceCode, userCode } = parsedVerificationUri

        if (!deviceCode || !userCode) {
            return throwError(
                new VoltError(VoltError.codes.FAILED_QR_CODE_VALIDATION_INVALID_CODE, {
                    backend: this.backendName,
                })
            )
        }

        return of({ deviceCode, userCode })
    }

    /**
     * Logout user
     * @returns {Observable<>} true = token
     */
    logout({ skipRevocation = false } = {}) {
        if (skipRevocation) {
            return super.logout()
        }

        return this.fetch({
            url: `${this.config.urls.authUrl}/oauth/revoketoken`,
            method: 'POST',
            headers: {
                'Content-Type': Constants.httpHeader.ContentType.APPLICATION_WWW_FORM_URLENCODED,
            },
            body: {
                access_token: DataHelper.getInstance().getPrimaryAccessToken(),
                client_id: this.config.clientId,
            },
            log: 'REVOKE TOKEN',
        }).pipe(
            mergeMap(() => {
                return super.logout()
            }),
            catchError((error) => {
                this.logger.warn('Error during logout... wipe cache and return success', error)
                return super.logout()
            })
        )
    }

    /**
     * Backend specific API: Check Validity of the Access Token
     * @returns {Observable<'active_token'|'invalid_token'|'expired_token'>} @link Constants.tokenStatus
     */
    checkAccessToken() {
        const accessToken = DataHelper.getInstance().getPrimaryAccessToken()
        if (!accessToken) {
            this.logger.warn('checkAccessToken', 'Missing Access Token')
            return of(Constants.tokenStatus.INVALID_TOKEN)
        }

        return this.fetch({
            url: `${this.config.urls.authUrl}/oauth/check_token`,
            method: 'POST',
            headers: {
                'Content-Type': Constants.httpHeader.ContentType.APPLICATION_WWW_FORM_URLENCODED,
            },
            body: {
                token: DataHelper.getInstance().getPrimaryAccessToken(),
            },
            log: 'CHECK TOKEN',
        }).pipe(
            mergeMap(({ response = {} } = {}) => {
                return of(
                    response.active === 'true'
                        ? Constants.tokenStatus.VALID_TOKEN
                        : Constants.tokenStatus.EXPIRED_TOKEN
                )
            }),
            catchError((error = {}) => {
                switch (error.code) {
                    case VoltError.codes.AUTH_OAUTH_EXPIRED_TOKEN.code:
                        this.logger.warn('Expired Access Token, need to be refreshed')
                        return of(Constants.tokenStatus.EXPIRED_TOKEN)
                    case VoltError.codes.INVALID_AUTH_TOKEN.code:
                        this.logger.warn('Invalid Access Token, need to sign in')
                        return of(Constants.tokenStatus.INVALID_TOKEN)
                    default:
                        this.logger.warn('Receive an unknown status', error)
                        return of(Constants.tokenStatus.UNKNOWN_TOKEN)
                }
            })
        )
    }

    /**
     * Login exteranlized though App Auth.
     * We receive only the oauthToken
     * @param {Object} data
     * @param {String} data.oauthToken Oauth Token
     * @param {String} data.oauthRefreshToken Refresh token
     * @param {String} data.oauthTokenExpirationDate Expiration Date (String format: "2022-07-16T16:46:52Z")
     * @returns {Observable<String>} Constants.authenticationStatus values
     */
    _loginFromAppAuth({ oauthToken, oauthRefreshToken, oauthTokenExpirationDate }) {
        if (!oauthToken || !oauthRefreshToken) {
            return throwError(
                new VoltError(VoltError.codes.LOGIN_FAILED, {
                    extraLog:
                        'Cannot login. Oauth TOKEN not provided by OIDC + PKCE (or expiration date or refresh token)',
                })
            )
        }
        // Use timestamp rather than Date to avoid UTC conversion error
        let expirationDate = new Date(oauthTokenExpirationDate)

        // If attributes provided, then we implicitly consider the user logged in
        return DataHelper.getInstance()
            .clearAllData()
            .pipe(
                mergeMap(() =>
                    DataHelper.getInstance()
                        .storeData([
                            [DataHelper.STORE_KEY.PRIMARY_ACCESS_TOKEN, oauthToken],
                            [
                                DataHelper.STORE_KEY.PRIMARY_ACCESS_TOKEN_EXPIRATION_TIME_MS,
                                expirationDate ? expirationDate.getTime() : Date.now() + 60 * 1000,
                            ],
                            [DataHelper.STORE_KEY.PRIMARY_ACCESS_TOKEN_ISSUED_TIME_MS, Date.now()],
                            [DataHelper.STORE_KEY.PRIMARY_REFRESH_TOKEN, oauthRefreshToken],
                        ])
                        .pipe(
                            switchMap(() => of(Constants.authenticationStatus.SUCCEEDED)),
                            tap(() => {
                                this._startAccessTokenRefreshTimer()
                            })
                        )
                )
            )
    }

    _refreshAccessToken() {
        return super._refreshAccessToken().pipe(mergeMap(() => this.gatewayApi.notifyUserLogin()))
    }

    /**
     * This function follows the nomenclature from VOLT API.
     * This function does not do the authentication but only check if storage contains
     * Token cached, then refresh the access token if the expirations occurred
     * Throw en error if there is not authentication token
     * @returns {Observable<Boolean>}
     */
    authenticate() {
        // STARHUB request systematically to check the Access Token at boot
        return DataHelper.getInstance()
            .readAllData()
            .pipe(
                mergeMap(() => {
                    return this.checkAccessToken().pipe(
                        mergeMap((tokenStatus) => {
                            /*
                            // For a period of monitoring, although backend response indicating token is invalid try to refresh the token
                            if (tokenStatus === Constants.tokenStatus.INVALID_TOKEN) {
                                return throwError(
                                    new VoltError(VoltError.codes.INVALID_AUTH_TOKEN, {
                                        extraLog: 'No valid Access token found',
                                    }),
                                )
                            }*/
                            const needRefreshToken =
                                tokenStatus !== Constants.tokenStatus.VALID_TOKEN
                            return this._authenticate(needRefreshToken)
                        })
                    )
                })
            )
    }

    /**
     * Out of home feature not supported
     * @returns {Observable<boolean>} False
     */
    getIsOutOfHome() {
        return of(false)
    }

    /**
     * Get the "suspended" state of current user
     * @returns {Observable<boolean>} true = is suspended otherwise false
     */
    getIsSuspended() {
        return of(false)
    }

    /**
     * Init Guest Mode
     * @returns {Observable<boolean>} True
     */
    initGuestMode() {
        return of(true)
    }

    // -----------------------------------------------------------
    // ----------                TOOLBOX                ----------
    // -----------------------------------------------------------
    /**
     * As Starhub product needs to use the Mobile Application for Scanning the QR Code
     * Then we need to be smart and adapt the URL with all information to the existing
     * legacy Mobile application (provided by Accedo) to let it authenticating the user.
     *
     * @param {OAuthDeviceFlowStatus} data.password
     * @returns {OAuthDeviceFlowStatus} Status with URI updated
     */
    _overloadVerificationUri(oauthStatus = {}) {
        if (oauthStatus) {
            const overloadedVerificationUri = JSON.stringify({
                deviceCode: oauthStatus.deviceCode,
                userCode: oauthStatus.userCode,
            })
            oauthStatus = oauthStatus.update({
                verificationUri: overloadedVerificationUri,
                verificationUriComplete: overloadedVerificationUri,
            })
        }
        return oauthStatus
    }

    _getStoredKeys() {
        let storedKeys = [
            ...super._getStoredKeys(),
            DataHelper.STORE_KEY.ESSOID,
            DataHelper.STORE_KEY.HUBID,
            DataHelper.STORE_KEY.PROFILE_TYPE,
        ]
        return storedKeys
    }

    // -----------------------------------------------------------
    // ---------- FEATURES NOT SUPPORTED BY THE BACKEND ----------
    // -----------------------------------------------------------
    loginWithSecureId(data) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[SIGN UP] Feature yet not implemented',
            })
        )
    }

    signUp(authFactor, data) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[SIGN UP] Feature yet not implemented',
            })
        )
    }

    validateOTP(authType, data) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[AUTH 2FA USING OTP] Feature yet not implemented',
            })
        )
    }

    isBackendAvailable = () => {
        return of(true)
    }
}
